import { call } from 'redux-saga/effects'
import {
  CHEC_PROD_HOST,
  SITEMINDER_QA_LOGIN,
  SITEMINDER_PROD_LOGIN,
} from 'utils/constants.js'
import { HANDLED, SKIP } from './constants'

/**
 * Known fetch-failure message substrings, one per major browser engine.
 *
 *   Chrome : "TypeError: Failed to fetch"
 *   Firefox: "TypeError: NetworkError when attempting to fetch resource."
 *   Safari : "TypeError: Load failed"
 *
 * We use a combination of `instanceof TypeError` AND a message-pattern check
 * rather than message alone (cross-realm resilience) or instanceof alone
 * (H5: other client-side TypeErrors — null access, bad destructuring — must
 * NOT trigger a SiteMinder redirect).
 */
const FETCH_FAILURE_PATTERNS =
  /failed to fetch|networkerror when attempting to fetch|load failed/i

/**
 * System handler — session expiry / network failure.
 *
 * A network failure (offline, CORS, CSP) or a SiteMinder session expiry
 * surfaces as a TypeError from the Fetch API. We identify it by combining
 * `instanceof TypeError` with a browser-specific message pattern so that
 * client-side TypeErrors (null dereference, bad response shapes, etc.) do NOT
 * accidentally trigger a full-page auth redirect.
 *
 * When this condition is met, we redirect to the SiteMinder login URL so the
 * user can re-authenticate. The `REACT_APP_SUPPRESS_ERR_REDIRECT` env flag
 * (must be the string `'true'`) disables the redirect in local development.
 *
 * M4: async-set cleanup is centralised in apiCall.js catch block — this
 * handler no longer dispatches removeFromAsyncSet itself.
 *
 * This is a SYSTEM handler and runs before any user-defined handlers.
 *
 * @param {Error} err - The caught error.
 * @returns {'handled'|'skip'}
 */
export default function* fetchFailureHandler(err) {
  // ── HTTP 401/403 — mid-session auth expiry ────────────────────────────────
  // HTTP auth failures arrive as NetworkError instances with err.status set
  // (not as TypeErrors), so they bypass the TypeError check below. Handle them
  // here in the system tier with the same SiteMinder redirect so auth expiry is
  // never silently swallowed by pageErrorHandler or shown as a generic modal.
  //
  // WHY THIS IS IN THE SYSTEM TIER (not defaultErrorHandler):
  // defaultErrorHandler treats GET failures as silent by design. Without this
  // guard, a 401/403 response to a GET request would produce no user-visible
  // feedback. pageErrorHandler explicitly returns SKIP for 401/403 to ensure
  // they bubble up, but that SKIP is belt-and-suspenders — this handler catches
  // them first (system tier always runs before user handlers), so they never
  // reach defaultErrorHandler at all.
  //
  // This prevents code reviewers from incorrectly concluding that 401/403 GET
  // failures are silently swallowed. They are redirected here.
  if (err.status === 401 || err.status === 403) {
    if (!['true', '1'].includes(process.env.REACT_APP_SUPPRESS_ERR_REDIRECT)) {
      const loginUrl =
        window.location.host === CHEC_PROD_HOST
          ? SITEMINDER_PROD_LOGIN
          : `${SITEMINDER_QA_LOGIN}?TARGET=$SM$${encodeURIComponent(
              window.location.href
            )}`
      yield call(() => {
        window.location = loginUrl
      })
    }
    return HANDLED
  }

  if (
    !(err instanceof TypeError) ||
    !FETCH_FAILURE_PATTERNS.test(err.message) ||
    // Accept both '1' and 'true' (string) so that .env files using either
    // conventional value suppress the redirect. CRA always stringifies env
    // vars, so a bare `true` in .env becomes the string 'true'. We also
    // accept '1' as an alternative to match common DevOps conventions.
    ['true', '1'].includes(process.env.REACT_APP_SUPPRESS_ERR_REDIRECT)
  ) {
    return SKIP
  }

  // This error means there was a problem with name resolution (offline),
  // Content Security Policy, or CORS.
  // See: https://github.com/github/fetch/issues/201#issuecomment-308213104
  //
  // yield call() wraps the side effect so saga tests can mock it via provide()
  // and avoid triggering actual navigation in unit test environments.
  const loginUrl =
    window.location.host === CHEC_PROD_HOST
      ? SITEMINDER_PROD_LOGIN
      : `${SITEMINDER_QA_LOGIN}?TARGET=$SM$${encodeURIComponent(
          window.location.href
        )}`

  yield call(() => {
    window.location = loginUrl
  })

  return HANDLED
}
